import React, { useRef, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import clsx from 'clsx';
import { reaction } from 'mobx';
import { ApplicationStore } from "../../../../store/ApplicationStore";
import { TimelineStore } from "../../../../store/TimelineStore";
import { convertAbsTimeToUnits, convertTimeToUnits } from "../../../../utils/dom/convertTimeToUnits";
import { convertUnitsToTime } from "../../../../utils/dom/convertUnitsToTime";
import { generateClipIdentifierClass } from "../../utils/generateClipIdentifierClass";
import { InteractiveClip, ResizeDirectionEnum } from "../../utils/InteractiveClip";
import { updateAdjacency } from "../../utils/updateLayerClipsAdjacency";
import { CompositionService } from "../../../../services/CompositionService";
import { ClipDragOverDividerEvent, TimelineEventsEnum } from "../../types/timeline.types";
import { CLIP_CLASSNAME, TIMELINE_TRACKS_CONTAINER_ID, TRACK_CLASSNAME } from "../../config/constants";
import './Clip.styles.scss';
import { TIMELINE_RULER_TICK_WIDTH_PX } from "../../../../config/config";
import {ContextMenuStore} from "../../../../store/ContextMenuStore";
import CopyIcon from "@mui/icons-material/ContentCopy";
import {TrashIcon} from "../../../../assets/icons/TrashIcon";
import {CompositionStateStore} from "../../../../store/CompositionStateStore";
import { ScriptLayerStore } from "../../../../store/ScriptLayerStore";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import {VolumeMutedIcon} from "../../../../../pages/NewAsset/player_icons";
import { TransitionHandle, TransitionConfig, VisualTransitionConfig, VisualTransitionType, WipeDirection } from "../TransitionHandle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
    Box,
    Popover,
    Typography,
    Select,
    MenuItem,
    TextField,
    InputAdornment,
    FormControl,
    Divider,
    Slider,
    ToggleButton,
    ToggleButtonGroup,
    IconButton,
} from '@mui/material';
import { secondsToTimecode } from "../../../../utils/secondsToTimecode";
import { beginScriptAddMediaFlow } from "../../../../utils/scriptAddMediaFlow";
import { findScriptSceneByTimelineRange, getScriptScenes, isScriptSceneEmpty } from "../../../../utils/scriptSceneUtils";
import { dispatchDuplicateRangeToNewComposition } from "../../../../utils/duplicateRangeToNewCompositionEvent";
import { buildScriptTimelineModel, findScriptTimelineSectionAtTime } from "../../../../utils/scriptTimelineSections";
import { ProjectAssetsStore } from "../../../../../state/ProjectAssetsStore";
import {
    dispatchRecordIntoTranscriptRequest,
    resolveRecordIntoTranscriptCompositionId,
    resolveScriptLocalTimeForRecording,
    resolveTimelineRecordAnchor,
} from "../../../../../pages/V2Editor/V2TextEditor/recordIntoTranscriptRequest";

const SNAP_THRESHOLD = 10;

/** Configuration for one side of a transition point */
export interface TransitionSideConfig {
    type: 'none' | 'fade' | 'crossfade';
    durationSec: number;
}

/** Transition point data for rendering */
export interface TransitionPointData {
    id: string;
    /** Position as fraction of clip duration (0-1) */
    position: number;
    /** Fade-out config (left side - end of segment A) */
    fadeOut: TransitionSideConfig;
    /** Fade-in config (right side - start of segment B) */
    fadeIn: TransitionSideConfig;
}

interface ClipProps {
    clipId: string;
    layerId: string;
    filename?: string;
    thumbnail?: string;
    isResizeEnabled?: boolean;
    backgroundColor?: string;
    clipVariant?: 'default' | 'media';
    clipKind?: 'audio' | 'video' | 'image' | 'captions' | 'text' | 'placeholder';
    isScriptLayerClip?: boolean;
    children?: React.ReactNode;
    /** Optional content to render in a dedicated words strip at the top of the clip */
    wordsContent?: React.ReactNode;
    /** Enable audio transition handles */
    showAudioTransitions?: boolean;
    /** Enable visual transition options (for video/image clips) */
    showVisualTransitions?: boolean;
    /** Current fade-in configuration */
    fadeInConfig?: TransitionConfig;
    /** Current fade-out configuration */
    fadeOutConfig?: TransitionConfig;
    /** Current visual fade-in configuration */
    visualFadeInConfig?: VisualTransitionConfig;
    /** Current visual fade-out configuration */
    visualFadeOutConfig?: VisualTransitionConfig;
    /** Whether there's an adjacent clip before this one (for crossfade option) */
    hasAdjacentBefore?: boolean;
    /** Whether there's an adjacent clip after this one (for crossfade option) */
    hasAdjacentAfter?: boolean;
    /** Callback when fade-in config changes */
    onFadeInChange?: (config: TransitionConfig) => void;
    /** Callback when fade-out config changes */
    onFadeOutChange?: (config: TransitionConfig) => void;
    /** Callback when visual fade-in config changes */
    onVisualFadeInChange?: (config: VisualTransitionConfig) => void;
    /** Callback when visual fade-out config changes */
    onVisualFadeOutChange?: (config: VisualTransitionConfig) => void;
    /** Internal transition points to render */
    transitionPoints?: TransitionPointData[];
    /** Callback when a transition point side config changes */
    onTransitionPointConfigChange?: (id: string, side: 'fadeOut' | 'fadeIn', config: TransitionSideConfig) => void;
    /** Callback when a transition point is removed */
    onTransitionPointRemove?: (id: string) => void;
}

export const Clip: React.FC<ClipProps> = observer(({ 
    clipId, 
    layerId, 
    filename = "Unknown", 
    thumbnail, 
    isResizeEnabled, 
    backgroundColor = "43,98,253",
    clipVariant = "default",
    clipKind,
    showVisualTransitions = false,
    visualFadeInConfig,
    visualFadeOutConfig,
    onVisualFadeInChange,
    onVisualFadeOutChange,
    children, 
    isScriptLayerClip, 
    wordsContent,
    showAudioTransitions = false,
    fadeInConfig,
    fadeOutConfig,
    hasAdjacentBefore = false,
    hasAdjacentAfter = false,
    onFadeInChange,
    onFadeOutChange,
    transitionPoints = [],
    onTransitionPointConfigChange,
    onTransitionPointRemove,
}) => {
    const [startTime, setStartTime] = useState(0);
    
    // Transition point popover state
    const [tpPopoverAnchor, setTpPopoverAnchor] = useState<HTMLElement | null>(null);
    const [selectedTpId, setSelectedTpId] = useState<string | null>(null);
    const [selectedTpSide, setSelectedTpSide] = useState<'fadeOut' | 'fadeIn'>('fadeOut');
    const [tpDurationText, setTpDurationText] = useState<string>('0');
    const [tpType, setTpType] = useState<'none' | 'fade' | 'crossfade'>('none');
    // Visual transition state for transition points
    const [tpVisualType, setTpVisualType] = useState<VisualTransitionType>('none');
    const [tpVisualDurationText, setTpVisualDurationText] = useState<string>('0');
    const [tpVisualWipeDirection, setTpVisualWipeDirection] = useState<WipeDirection>('left');
    const [tpVisualBlurAmount, setTpVisualBlurAmount] = useState<number>(50);
    const [duration, setDuration] = useState(0);
    const [trimmedDuration, setTrimmedDuration] = useState(0);
    const [width, setWidth] = useState(200);

    const xRef = useRef(0);
    const yRef = useRef(0);

    const startTimeRef = useRef(0);
    const durationRef = useRef(0);
    

    const [x, setX] = useState(0);
    const [y, setY] = useState(0);

    const [isFocused, setIsFocused] = useState(false);
    const [hasSuccessor, setHasSuccessor] = useState(false);
    const [hasPredecessor, setHasPredecessor] = useState(false);
    const [isSlipHovering, setIsSlipHovering] = useState(false);

    const elementRef = useRef<HTMLDivElement>(null);
    const interactionRef = useRef<InteractiveClip | null>(null);
    const initialPositionRef = useRef({ top: 0, height: 0, left: 0, width: 0, x: 0, clientXOffset: 0, clientYOffset: 0 });
    const layerSnapPositionsRef = useRef<{ top: number; height: number; layerId: string; canDrop: boolean }[]>([]);
    const clipSnapPositionsRef = useRef<{ x: number; left: number; width: number; clipId: string }[]>([]);
    const focusEventRef = useRef<MouseEvent | null>(null);
    const slipStateRef = useRef<{
        startClientX: number;
        baseInSec: number;
        baseOutSec: number;
        baseStartSec: number;
        durationSec: number;
        playbackRate: number;
    } | null>(null);
    const slipRecomputeRafRef = useRef<number | null>(null);

    const clipStyle = backgroundColor ? {
        '--clip-background-color': backgroundColor
    } as React.CSSProperties : {};

    const isSlipMode = Boolean(ApplicationStore.slipToolEnabled) && !isScriptLayerClip;
    const engineClipForSlip: any = isSlipMode ? CompositionService.getClipById(clipId) : null;
    const isSlipTarget = Boolean(isSlipMode && engineClipForSlip && 'range' in engineClipForSlip && 'offset' in engineClipForSlip);
    const isSlipDragging = Boolean(TimelineStore.draggedClips?.[clipId]);
    const slipRangeText = (() => {
        if (!isSlipTarget) return null;
        const inSec = Number(engineClipForSlip?.range?.[0]?.seconds ?? 0);
        const outSec = Number(engineClipForSlip?.range?.[1]?.seconds ?? 0);
        if (!Number.isFinite(inSec) || !Number.isFinite(outSec)) return null;
        return `${secondsToTimecode(inSec, false, false)} | ${secondsToTimecode(outSec, false, false)}`;
    })();

    useEffect(() => {
        const disposeReaction = reaction(
            () => ({
                zoom: TimelineStore.zoom,
                baseStartSec: TimelineStore.baseStartSec,
            }),
            () => {
                const clip = CompositionStateStore.clips?.[clipId];
                if (clip == null) return;
                xRef.current = convertAbsTimeToUnits(clip.startTime);
                setX(xRef.current);
                setWidth(convertTimeToUnits(clip.trimmedDuration));
            }
        );

        return () => {
            disposeReaction();
        };
    }, [clipId]);

    useEffect(() => {
        const disposer = reaction(
            () => ({
                clip: CompositionStateStore.clips?.[clipId],
                selectedClipId: ApplicationStore.selectedClipId,
            }),
            ({ clip, selectedClipId }) => {
                if (!clip) return;
                startTimeRef.current = clip.startTime;
                setStartTime(clip.startTime);
                durationRef.current = clip.duration;
                setDuration(clip.duration);
                setTrimmedDuration(clip.trimmedDuration);
                setHasPredecessor(clip.hasPredecessor ?? false);
                setHasSuccessor(clip.hasSuccessor ?? false);
                setIsFocused(selectedClipId === clip.id);
            },
            { fireImmediately: true }
        );

        return () => {
            disposer();
        };
    }, [clipId]);

    useEffect(() => {
        xRef.current = convertAbsTimeToUnits(startTime);
        setX(xRef.current);
        setWidth(convertTimeToUnits(trimmedDuration));
        updateAdjacency(layerId);
    }, [startTime, trimmedDuration, layerId]);

    useEffect(() => {
        if (elementRef.current == null || elementRef.current.parentElement?.parentElement == null) return;

        interactionRef.current = new InteractiveClip(elementRef.current, elementRef.current.parentElement.parentElement, {
            onDragStart: dragStart,
            onResizeStart: resizeStart,
            onDrag: drag,
            onResize: resize,
            onResizeEnd: resizeEnd,
            onDragEnd: dragEnd,
            moveableClassName: "libretto-timeline-clip__moveable",
        });

        handleUpdateStyles();

        return () => {
            interactionRef.current?.destroy();
        };
    }, []);

    useEffect(() => {
        handleUpdateStyles();
    }, [hasPredecessor, hasSuccessor, width, xRef.current, yRef.current]);

	    const handleFocusChange = () => {
	        // Script layer clip is the composition spine: it is not draggable/resizable.
	        if (isScriptLayerClip) {
	            interactionRef.current?.destroy();
	            focusEventRef.current = null;
	            if (elementRef.current != null) {
	                elementRef.current.style.zIndex = isFocused ? "2" : "1";
	            }
	            return;
	        }

	        if (interactionRef.current == null) {
	            // give the interaction enough time to mount
	            setTimeout(() => {
	                handleFocusChange();
	            });
            return;
        }

        if (isFocused && !ApplicationStore.rangeToolEnabled) {
            const resizeEnabled = !ApplicationStore.slipToolEnabled && (isResizeEnabled ?? true);
            // @ts-ignore
            interactionRef.current.mount(focusEventRef.current, { isResizeEnabled: resizeEnabled });
        } else {
            interactionRef.current.destroy();
            focusEventRef.current = null;
        }

        if (elementRef.current != null) {
            elementRef.current.style.zIndex = isFocused ? "2" : "1";
        }
    };

    useEffect(() => {
        handleFocusChange();
    }, [isFocused, ApplicationStore.rangeToolEnabled, ApplicationStore.slipToolEnabled, isResizeEnabled]);

    useEffect(() => {
        return () => {
            if (slipRecomputeRafRef.current != null) {
                cancelAnimationFrame(slipRecomputeRafRef.current);
                slipRecomputeRafRef.current = null;
            }
        };
    }, []);

    const handleUpdateStyles = () => {
        if (elementRef.current == null) return;

        if (hasPredecessor && hasSuccessor) {
            elementRef.current.style.width = `calc(${width}px - 2px)`;
            elementRef.current.style.transform = `translate(calc(${xRef.current}px + 1px), ${yRef.current}px)`;
        } else if (hasSuccessor) {
            elementRef.current.style.width = `calc(${width}px - 1px)`;
            elementRef.current.style.transform = `translate(${xRef.current}px, ${yRef.current}px)`;
        } else if (hasPredecessor) {
            elementRef.current.style.width = `calc(${width}px - 1px)`;
            elementRef.current.style.transform = `translate(calc(${xRef.current}px + 1px), ${yRef.current}px)`;
        } else {
            elementRef.current.style.width = `${width}px`;
            elementRef.current.style.transform = `translate(${xRef.current}px, ${yRef.current}px)`;
        }

        interactionRef.current?.moveable?.updateRect();
    };

    const drag = (clientX: number, clientY: number) => {

        // Slip tool: drag shifts source in/out without moving the clip on the timeline.
        // NOTE: This handler is mounted once via InteractiveClip, so we must read slip state
        // directly from ApplicationStore (not from render-derived booleans) to avoid stale closures.
        if (ApplicationStore.slipToolEnabled && !isScriptLayerClip) {
            const st = slipStateRef.current;
            if (!st) return; // In slip mode, never move the clip.
            const pxPerSec = TIMELINE_RULER_TICK_WIDTH_PX * (TimelineStore.zoom ?? 1);
            if (!Number.isFinite(pxPerSec) || pxPerSec <= 0) return;

            const deltaPx = clientX - st.startClientX;
            // Slip UX: dragging left reveals later source time (increases inSec).
            const deltaTimelineSec = -deltaPx / pxPerSec;
            const windowLen = Math.max(0, st.baseOutSec - st.baseInSec);
            if (windowLen <= 0) return;

            const maxIn = Math.max(0, (st.durationSec || 0) - windowLen);
            const deltaSourceSec = deltaTimelineSec * (st.playbackRate || 1);
            const desiredIn = Math.max(0, Math.min(maxIn, st.baseInSec + deltaSourceSec));
            const desiredOut = desiredIn + windowLen;
            const desiredOffset = st.baseStartSec - desiredIn;

            CompositionService.setMediaClipWindow(
                clipId,
                { inSec: desiredIn, outSec: desiredOut, offsetSec: desiredOffset },
                { transient: true },
            );

            // Re-render canvas at current playhead so the user sees the slipped content.
            if (slipRecomputeRafRef.current == null) {
                slipRecomputeRafRef.current = requestAnimationFrame(() => {
                    slipRecomputeRafRef.current = null;
                    try { CompositionService.recomputeFrame(); } catch {}
                });
            }

            return;
        }

        const deltaX = clientX - initialPositionRef.current.left;
        let newX = initialPositionRef.current.x + deltaX - initialPositionRef.current.clientXOffset;
        let nextY = clientY - initialPositionRef.current.top - initialPositionRef.current.clientYOffset;

        const { dividerIndex, layerSnapPositionIndex } = findLayerSnapPosition(clientY);

        const currentClipLeft = newX;
        const currentClipRight = currentClipLeft + width;

        for (let i = 0; i < clipSnapPositionsRef.current.length; i++) {
            const { x: clipX, width: clipWidth } = clipSnapPositionsRef.current[i];
            const clipRight = clipX + clipWidth;

            const diffLeft = Math.abs(currentClipLeft - clipX);
            const diffRight = Math.abs(currentClipRight - clipRight);
            const diffRightLeft = Math.abs(currentClipRight - clipX);
            const diffLeftRight = Math.abs(currentClipLeft - clipRight);

            if (diffLeft <= SNAP_THRESHOLD) {
                newX = clipX;
                break;
            } else if (diffRight <= SNAP_THRESHOLD) {
                newX = clipRight - width;
                break;
            } else if (diffRightLeft <= SNAP_THRESHOLD) {
                newX = clipX - width;
                break;
            } else if (diffLeftRight <= SNAP_THRESHOLD) {
                newX = clipRight;
                break;
            }
        }

        if (layerSnapPositionIndex !== -1) {
            nextY = layerSnapPositionsRef.current[layerSnapPositionIndex].top - initialPositionRef.current.top;
            dispatchClipDragOverDividerEvent();
        }

        if (dividerIndex !== -1) {
            dispatchClipDragOverDividerEvent(layerSnapPositionsRef.current.length - dividerIndex);
        }

        yRef.current = nextY;
        setY(nextY);
        xRef.current = newX;
        setX(newX);
    };

    const resize = (clientX: number, direction: ResizeDirectionEnum) => {
        if (CompositionStateStore.clips?.[clipId]?.type === "subtitles") {
            return;
        }

        const MARGIN_PADDING = 6;

        let deltaX = 0;
        let newWidth = initialPositionRef.current.width;

        if (direction === ResizeDirectionEnum.LEFT) {
            deltaX = clientX - initialPositionRef.current.left - MARGIN_PADDING;
            newWidth = initialPositionRef.current.left + initialPositionRef.current.width - clientX + MARGIN_PADDING;
        } else if (direction === ResizeDirectionEnum.RIGHT) {
            newWidth = clientX - initialPositionRef.current.left + MARGIN_PADDING;
            deltaX = 0;
        }

        let newX = initialPositionRef.current.x + deltaX;
        const currentClipLeft = newX;
        const currentClipRight = currentClipLeft + newWidth;

        for (let i = 0; i < clipSnapPositionsRef.current.length; i++) {
            const { x: clipX, width: clipWidth } = clipSnapPositionsRef.current[i];
            const clipRight = clipX + clipWidth;

            const diffLeft = Math.abs(currentClipLeft - clipX);
            const diffRight = Math.abs(currentClipRight - clipRight);
            const diffRightLeft = Math.abs(currentClipRight - clipX);
            const diffLeftRight = Math.abs(currentClipLeft - clipRight);

            if (direction === ResizeDirectionEnum.LEFT) {
                if (diffLeft <= SNAP_THRESHOLD) {
                    newX = clipX;
                    newWidth = initialPositionRef.current.width + (initialPositionRef.current.x - newX);
                    break;
                } else if (diffLeftRight <= SNAP_THRESHOLD) {
                    newX = clipRight;
                    newWidth = initialPositionRef.current.width + (initialPositionRef.current.x - newX);
                    break;
                }
            } else if (direction === ResizeDirectionEnum.RIGHT) {
                if (diffRight <= SNAP_THRESHOLD) {
                    newWidth = clipRight - currentClipLeft;
                    break;
                } else if (diffRightLeft <= SNAP_THRESHOLD) {
                    newWidth = clipX - currentClipLeft;
                    break;
                }
            }
        }

        // Prevent visual positioning before timeline start
        if (direction === ResizeDirectionEnum.LEFT) {
            // Recompute width if clamping left edge to 0
            if (newX < 0) {
                newWidth = currentClipRight - 0;
                newX = 0;
            }
        }
        xRef.current = Math.max(0, newX);
        setX(newX);
        setWidth(newWidth);
    };

    const resizeStart = () => {
        if (CompositionStateStore.clips?.[clipId]?.type === "subtitles") {
            return;
        }

        if (!elementRef.current) {
            return;
        }

        const { top, height, left, width } = elementRef.current.getBoundingClientRect();
        initialPositionRef.current = { top, height, left, width, x: xRef.current, clientXOffset: 0, clientYOffset: 0 };

        TimelineStore.setDraggingClip(clipId, true);
        setClipSnapPositions();
    };

    const resizeEnd = (direction: ResizeDirectionEnum, distance: number) => {
        const clip = CompositionService.getClipById(clipId);
        if (!clip) return;

        if (CompositionStateStore.clips?.[clipId]?.type === "subtitles") {
            return;
        }

        const distanceToTime = convertUnitsToTime(distance);
        if (direction === ResizeDirectionEnum.LEFT) {
            CompositionService.trimClipLeft(clipId, distanceToTime);
        } else if (direction === ResizeDirectionEnum.RIGHT) {
            CompositionService.trimClipRight(clipId, distanceToTime);
        }
        TimelineStore.setDraggingClip(clipId, false);

        clipSnapPositionsRef.current = [];
        handlePostUpdateClip();
    };

    const dragStart = (clientX: number, clientY: number) => {
        if (!elementRef.current) {
            return;
        }

        // Slip tool: drag shifts source in/out without moving the clip on the timeline.
        // NOTE: This handler is mounted once via InteractiveClip, so we must read slip state
        // directly from ApplicationStore (not from render-derived booleans) to avoid stale closures.
        if (ApplicationStore.slipToolEnabled && !isScriptLayerClip) {
            const clip: any = CompositionService.getClipById(clipId);
            const type = String(clip?.type ?? "").toLowerCase();
            const isSlippableType = type === "video" || type === "audio";
            if (!clip || !isSlippableType || !("range" in clip) || !("offset" in clip)) {
                slipStateRef.current = null;
                return; // In slip mode, never start a normal drag.
            }
            const inSec = Number(clip.range?.[0]?.seconds ?? 0);
            const outSec = Number(clip.range?.[1]?.seconds ?? inSec);
            const startSec = Number(clip.start?.seconds ?? (clip.offset?.seconds ?? 0) + inSec);
            const durationSec = Number(clip.duration?.seconds ?? 0);
            const playbackRate = Number(clip.playbackRate ?? 1) || 1;
            slipStateRef.current = {
                startClientX: clientX,
                baseInSec: Number.isFinite(inSec) ? inSec : 0,
                baseOutSec: Number.isFinite(outSec) ? outSec : 0,
                baseStartSec: Number.isFinite(startSec) ? startSec : 0,
                durationSec: Number.isFinite(durationSec) ? durationSec : 0,
                playbackRate: Number.isFinite(playbackRate) ? playbackRate : 1,
            };
            TimelineStore.setDraggingClip(clipId, true);
            return;
        }

        const { top, height, left, width } = elementRef.current.getBoundingClientRect();

        initialPositionRef.current = {
            top,
            height,
            left,
            width,
            x: xRef.current,
            clientXOffset: clientX - left,
            clientYOffset: clientY - top,
        };

        TimelineStore.setDraggingClip(clipId, true);
        setLayerSnapPositions();
        setClipSnapPositions();
    };

    const dragEnd = (clientX: number, clientY: number) => {
        // Slip tool: finalize the source window shift (single persisted update).
        // NOTE: This handler is mounted once via InteractiveClip, so we must read slip state
        // directly from ApplicationStore (not from render-derived booleans) to avoid stale closures.
        if (ApplicationStore.slipToolEnabled && !isScriptLayerClip) {
            const st = slipStateRef.current;
            slipStateRef.current = null;
            TimelineStore.setDraggingClip(clipId, false);
            if (!st) return; // In slip mode, never run normal dragEnd.

            const pxPerSec = TIMELINE_RULER_TICK_WIDTH_PX * (TimelineStore.zoom ?? 1);
            if (!Number.isFinite(pxPerSec) || pxPerSec <= 0) return;

            const deltaPx = clientX - st.startClientX;
            // Slip UX: dragging left reveals later source time (increases inSec).
            const deltaTimelineSec = -deltaPx / pxPerSec;
            const windowLen = Math.max(0, st.baseOutSec - st.baseInSec);
            if (windowLen <= 0) return;

            const maxIn = Math.max(0, (st.durationSec || 0) - windowLen);
            const deltaSourceSec = deltaTimelineSec * (st.playbackRate || 1);
            const desiredIn = Math.max(0, Math.min(maxIn, st.baseInSec + deltaSourceSec));
            const desiredOut = desiredIn + windowLen;
            const desiredOffset = st.baseStartSec - desiredIn;

            CompositionService.setMediaClipWindow(
                clipId,
                { inSec: desiredIn, outSec: desiredOut, offsetSec: desiredOffset },
                { transient: false },
            );
            try { CompositionService.recomputeFrame(); } catch {}
            return;
        }

        const { dividerIndex, layerSnapPositionIndex } = findLayerSnapPosition(clientY);
        dispatchClipDragOverDividerEvent();

        if (layerSnapPositionIndex !== -1) {
            yRef.current = layerSnapPositionsRef.current[layerSnapPositionIndex].top - initialPositionRef.current.top;

            const { layerId: newLayerId } = layerSnapPositionsRef.current[layerSnapPositionIndex];

            // Use current engine layer id to avoid stale prop after layer deletion/reindexing
            const currentLayerId = CompositionService.getClipById(clipId)?.track?.id;
            if (currentLayerId && currentLayerId !== newLayerId) {
                void CompositionService.moveClipToLayer(clipId, newLayerId).then((moved) => {
                    if (!moved) {
                        // Rejected (e.g. audio↔visual track mismatch). Snap back visually.
                        yRef.current = 0;
                        handleUpdateStyles();
                    }
                });
            }
        } else {
            yRef.current = 0;
            setY(0);

            console.log("No layer snap position found")

            // CompositionService.createLayer(layerSnapPositionsRef.current.length - dividerIndex, clipId).then((newLayer) => {
            //     CompositionService.moveClipToLayer(clipId, newLayer.id);
            // });
        }

        handlePostUpdateClip();

        layerSnapPositionsRef.current = [];
        clipSnapPositionsRef.current = [];
    };

    const dispatchClipDragOverDividerEvent = (index: number = -1) => {
        const event = new CustomEvent<ClipDragOverDividerEvent>(TimelineEventsEnum.CLIP_DRAG_OVER_DIVIDER, {
            detail: { clipId, index },
        });
        document.dispatchEvent(event);
    };

    const findLayerSnapPosition = (clientY: number) => {
        let dividerIndex = -1;
        let layerSnapPositionIndex = -1;

        if (!layerSnapPositionsRef.current.length) {
            return { dividerIndex, layerSnapPositionIndex };
        }

        const firstSnapPosition = layerSnapPositionsRef.current[0];
        const lastSnapPosition = layerSnapPositionsRef.current[layerSnapPositionsRef.current.length - 1];

        if (clientY <= firstSnapPosition.top) {
            dividerIndex = 0;
        }

        if (clientY >= lastSnapPosition.top + lastSnapPosition.height) {
            dividerIndex = layerSnapPositionsRef.current.length;
        }

        if (dividerIndex > -1) {
            return { dividerIndex, layerSnapPositionIndex };
        }

        for (let i = 0; i < layerSnapPositionsRef.current.length; i++) {
            const { top: trackTop, height: trackHeight, canDrop } = layerSnapPositionsRef.current[i];
            const nextPosition = layerSnapPositionsRef.current[i + 1];

            if (clientY >= trackTop && clientY <= trackTop + trackHeight) {
                if (canDrop) layerSnapPositionIndex = i;
                break;
            }

            if (clientY >= trackTop + trackHeight && clientY <= nextPosition?.top) {
                dividerIndex = i + 1;
                break;
            }
        }

        return { dividerIndex, layerSnapPositionIndex };
    };

    const setLayerSnapPositions = () => {
        const tracks = document.querySelectorAll(`.${TRACK_CLASSNAME}`);
        const currentLayerId = CompositionService.getClipById(clipId)?.track?.id;
        layerSnapPositionsRef.current = Array.from(tracks).map((track) => {
            // @ts-ignore
            const { top, height } = track.getBoundingClientRect();
            // @ts-ignore
            const layerId = track.id;
            const canDrop = layerId === currentLayerId || CompositionService.canMoveClipToLayer(clipId, layerId);
            return { top, height, layerId, canDrop };
        });
    };

    const setClipSnapPositions = () => {
        const clips = document.querySelectorAll(`.${CLIP_CLASSNAME}`);
        const tracksContainer = document.getElementById(TIMELINE_TRACKS_CONTAINER_ID);
        const offsetLeft = tracksContainer?.offsetLeft ?? 0;

        // @ts-ignore
        // @ts-ignore

        clipSnapPositionsRef.current = Array.from(clips)
            // @ts-ignore
            .filter((clip) => clip.id !== clipId)
            .map((clip) => {
                // @ts-ignore
                const { left, width } = clip.getBoundingClientRect();
                // @ts-ignore
                return { left, width, x: left - offsetLeft, clipId: clip.id };
            });
    };

    const handlePostUpdateClip = () => {
        // New engine path: start time is independent of left trim
        const desiredStart = convertUnitsToTime(xRef.current);

        const actualStart = CompositionService.moveClipTo(clipId, desiredStart);

        // update local state for immediate UX
        startTimeRef.current = actualStart;
        setStartTime(actualStart);

        const uiClip = CompositionStateStore.clips?.[clipId];
        const nextStart = uiClip?.startTime ?? actualStart;
        const nextWidth = convertTimeToUnits(uiClip?.trimmedDuration ?? durationRef.current);

        xRef.current = convertAbsTimeToUnits(nextStart);
        setX(xRef.current);
        setWidth(nextWidth);
        TimelineStore.setDraggingClip(clipId, false);
        // After moves (including cross-layer), recompute adjacency on the clip's CURRENT layer,
        // not the original prop. This avoids stale layerId after layer deletions/reindexing.
        const currentLayerId = CompositionService.getClipById(clipId)?.track?.id ?? layerId;
        updateAdjacency(currentLayerId);
        // TODO: adjustClipsLayout equivalent can be called here later if needed
    };

	    const handleMouseDown = (event: React.MouseEvent) => {
	        // Only handle left-click (button 0) for selection and playhead
	        // Right-click (button 2) is handled by handleContextMenu
	        if (event.button !== 0) return;

	        event.preventDefault();
	        event.stopPropagation();

	        // Range tool mode: don't seek or scene-select; just focus the clip so range ops apply to it.
	        if (ApplicationStore.rangeToolEnabled) {
	            if (ApplicationStore.selectedClipId !== clipId) {
	                ApplicationStore.setSelectedClipId(clipId);
	            }
	            return;
	        }

	        // Slip tool mode (non-script clips): don't seek; just focus so dragging slips source in/out.
	        if (ApplicationStore.slipToolEnabled && !isScriptLayerClip) {
	            if (!isFocused) {
	                focusEventRef.current = event.nativeEvent;
	            }
	            if (ApplicationStore.selectedClipId !== clipId) {
	                ApplicationStore.setSelectedClipId(clipId);
	            }
	            return;
	        }

	        // Move playhead to clicked position within the clip
	        if (elementRef.current) {
				const interactionElement = elementRef.current.querySelector<HTMLElement>(
					'.script-clip__interaction-layer',
				);
				const rect = (interactionElement ?? elementRef.current).getBoundingClientRect();
	            const relX = event.clientX - rect.left;
	            const ratio = rect.width > 0 ? Math.max(0, Math.min(1, relX / rect.width)) : 0;
            // IMPORTANT: Prefer store duration over local React state to avoid "first click" mismatch
            // when CompositionStateStore updates but React state hasn't flushed yet.
            const storeDur = CompositionStateStore.clips?.[clipId]?.trimmedDuration;
            const dur =
                typeof storeDur === 'number' && Number.isFinite(storeDur) && storeDur > 0
                    ? storeDur
                    : (trimmedDuration || durationRef.current || 0);
            const clickedTime = startTime + (ratio * dur);
            CompositionService.seek(clickedTime);
        }

        // ScriptClip normally owns this interaction through its full-bleed
        // pointer layer. Keep this border-click fallback on the same model.
        if (isScriptLayerClip && elementRef.current) {
            const storeDur = CompositionStateStore.clips?.[clipId]?.trimmedDuration;
            const dur =
                typeof storeDur === 'number' && Number.isFinite(storeDur) && storeDur > 0
                    ? storeDur
                    : (trimmedDuration || durationRef.current || 0);
            if (dur > 0) {
                const rect = elementRef.current.getBoundingClientRect();
                const relX = event.clientX - rect.left;
                const pct = rect.width > 0 ? (relX / rect.width) : 0;
	                const timeAtMouse = Math.max(0, Math.min(dur, pct * dur));
	                const engineClip: any = CompositionService.getClipById(clipId);
	                const model = buildScriptTimelineModel({
	                    clip: engineClip,
	                    duration: dur,
	                    mapSourceBoundaryToTimeline: (sourceTime) =>
	                        ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(sourceTime),
	                });
	                const clickedSection = findScriptTimelineSectionAtTime(model.sections, timeAtMouse);
	                if (!clickedSection) return;

	                if (!isFocused) {
	                    focusEventRef.current = event.nativeEvent;
	                    ApplicationStore.setSelectedClipId(clipId);
	                }
	                ApplicationStore.selectScriptTimelineSection(clickedSection, model.sections);
	                return;
	            }
	        }

        // For non-script clips, only handle if not already focused
        if (isFocused) {
            return;
        }

        focusEventRef.current = event.nativeEvent;
        ApplicationStore.setSelectedClipId(clipId);
    };

		    const handleContextMenu = (e) => {
		        e.preventDefault();
		        e.stopPropagation();

		        // Range tool context menu: when a range is selected, ONLY show range actions.
		        if (ApplicationStore.rangeToolEnabled && ApplicationStore.selectedClipId === clipId) {
		            const range = isScriptLayerClip
		                ? ApplicationStore.selectedScriptLayerRange
		                : ApplicationStore.selectedRange;

		            if (range) {
		                const start = Math.min(Number(range.start ?? 0), Number(range.end ?? 0));
		                const end = Math.max(Number(range.start ?? 0), Number(range.end ?? 0));
		                const EPS = 1e-3;

		                const items: any[] = [];

		                if (isScriptLayerClip) {
		                    if (Number.isFinite(start) && Number.isFinite(end) && Math.abs(end - start) > EPS) {
		                        items.push({
		                            title: "Duplicate to new composition",
		                            onClick: () => {
		                                dispatchDuplicateRangeToNewComposition({
		                                    startSec: start,
		                                    endSec: end,
		                                    source: "clip-range-menu",
		                                });
		                                ContextMenuStore.hide();
		                            },
		                        });
		                    }

		                    // Resolve the whole interval at once. This clips either endpoint
		                    // inward across inserted silence of any length and respects each
		                    // segment's playback rate.
		                    const sourceRange = ScriptLayerStore.mapTimelineRangeToSourceRange(start, end);

		                    if (sourceRange) {
		                        const s = sourceRange.start;
		                        const e2 = sourceRange.end;
		                        items.push(
		                            {
		                                title: "Replace with gap clip",
		                                onClick: () => {
		                                    CompositionService.addScriptLayerGap(s, e2);
		                                    ApplicationStore.setSelectedScriptLayerRange(null);
		                                    ContextMenuStore.hide();
		                                },
		                            },
		                            {
		                                title: "Delete",
		                                onClick: () => {
		                                    CompositionService.cutScriptLayerRange(s, e2, { transcriptMode: "delete" });
		                                    ApplicationStore.setSelectedScriptLayerRange(null);
		                                    ContextMenuStore.hide();
		                                },
		                            }
		                        );
		                    } else {
		                        // Still show the menu (range tool invariant) even if we can't map the selection.
		                        items.push(
		                            { title: "Replace with gap clip", onClick: () => ContextMenuStore.hide() },
		                            { title: "Delete", onClick: () => ContextMenuStore.hide() }
		                        );
		                    }
		                } else {
		                    // Non-script clip selections are relative to the clip start; convert to absolute composition time.
		                    const storeDur = CompositionStateStore.clips?.[clipId]?.trimmedDuration;
		                    const dur =
		                        typeof storeDur === 'number' && Number.isFinite(storeDur) && storeDur > 0
		                            ? storeDur
		                            : (trimmedDuration || durationRef.current || 0);
		                    const clipStart = Number(CompositionStateStore.clips?.[clipId]?.startTime ?? startTimeRef.current ?? 0);

		                    const clampedStartRel = Math.max(0, Math.min(dur, start));
		                    const clampedEndRel = Math.max(0, Math.min(dur, end));
		                    const absStart = clipStart + Math.min(clampedStartRel, clampedEndRel);
		                    const absEnd = clipStart + Math.max(clampedStartRel, clampedEndRel);

		                    if (Number.isFinite(absStart) && Number.isFinite(absEnd) && Math.abs(absEnd - absStart) > EPS) {
		                        items.push(
		                            {
		                                title: "Replace with gap clip",
		                                onClick: () => {
		                                    void CompositionService.replaceMediaRangeWithGap(clipId, absStart, absEnd);
		                                    ApplicationStore.setSelectedRange(null);
		                                    ContextMenuStore.hide();
		                                },
		                            },
		                            {
		                                title: "Delete",
		                                onClick: () => {
		                                    CompositionService.trimClip(clipId, absStart, absEnd);
		                                    ApplicationStore.setSelectedRange(null);
		                                    ContextMenuStore.hide();
		                                },
		                            }
		                        );
		                    } else {
		                        items.push(
		                            { title: "Replace with gap clip", onClick: () => ContextMenuStore.hide() },
		                            { title: "Delete", onClick: () => ContextMenuStore.hide() }
		                        );
		                    }
		                }

		                ContextMenuStore.show(e.clientX, e.clientY, items);
		                return;
		            }
		        }

		        // Note: For ScriptLayerClips we DO select the clicked section on right-click so
		        // inserted-gap sections get a correct context menu + highlight.
		        // For other clips, we intentionally do NOT select on right-click.

	        const menuItems = [];
	        const engineClip: any = CompositionService.getClipById(clipId);
	        const isVisualClip = CompositionService.isVisualClip(engineClip);
	        let contextSeekTimeSec: number | null = null;
	        const storeDur = CompositionStateStore.clips?.[clipId]?.trimmedDuration;
	        const dur =
	            typeof storeDur === 'number' && Number.isFinite(storeDur) && storeDur > 0
	                ? storeDur
	                : (trimmedDuration || durationRef.current || 0);

	            // Right-click seek is disabled while range tool is enabled to avoid disrupting range selection UX.
	            if (!ApplicationStore.rangeToolEnabled) {
	                try {
	                    if (elementRef.current && dur > 0) {
	                        const rect = elementRef.current.getBoundingClientRect();
	                        const relX = e.clientX - rect.left;
	                        const pct = rect.width > 0 ? Math.max(0, Math.min(1, relX / rect.width)) : 0;
	                        const timeWithinClip = Math.max(0, Math.min(dur, pct * dur));
	                        const clipStart = Number(CompositionStateStore.clips?.[clipId]?.startTime ?? startTimeRef.current ?? 0);
			                    contextSeekTimeSec = clipStart + timeWithinClip;
	                        void CompositionService.seek(contextSeekTimeSec);
	                    }
	                } catch {}
	            }

	        const getSceneStartAtTime = (timelineTimeSec: number): number | null => {
	            try {
	                const scriptClip: any = CompositionService.getScriptLayerClip?.();
	                if (!scriptClip) return null;
	                const EPS = 1e-3;
	                const inSec = Number(scriptClip.range?.[0]?.seconds ?? 0);
	                const outSec = Number(scriptClip.range?.[1]?.seconds ?? scriptClip.duration?.seconds ?? inSec);
	                const splitsSrc: number[] = (scriptClip.splits ?? [])
	                    .filter((s: any) => s?.isSceneBoundary === true)
	                    .map((s: any) => Number(s.timeSec))
	                    .filter((t: any) => Number.isFinite(t) && t > inSec + EPS && t < outSec - EPS)
	                    .sort((a: number, b: number) => a - b);

	                let srcTime = ScriptLayerStore.mapTimelineTimeToSourceTime(timelineTimeSec);
	                if (srcTime == null) {
	                    const rate = Number(ScriptLayerStore.playbackRate || 1) || 1;
	                    const clipStartSec = Number(scriptClip.start?.seconds ?? 0);
	                    const absCompTime = clipStartSec + timelineTimeSec * rate;
	                    const segs: any[] = Array.isArray(scriptClip.scriptSegments) ? scriptClip.scriptSegments : [];
	                    const nextSeg = segs.find((s: any) => Number(s?.compStartSec ?? 0) > absCompTime + EPS);
	                    if (nextSeg && Number.isFinite(nextSeg.srcStartSec)) {
	                        srcTime = Number(nextSeg.srcStartSec);
	                    }
	                }
	                if (srcTime == null || !Number.isFinite(srcTime)) return null;

	                let prevSplit = inSec;
	                for (const t of splitsSrc) {
	                    if (t <= (srcTime as number) + EPS) prevSplit = t;
	                    else break;
	                }

	                const mapped = ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(prevSplit);
	                return mapped != null && Number.isFinite(mapped) ? Number(mapped) : null;
	            } catch {
	                return null;
	            }
	        };

        // Helper to build Layer order submenu (only for visual clips)
        // For ScriptLayerClips with a selected source, operate on the source.
        // For regular clips, operate on the whole track.
        const selectedSourceIndex = ApplicationStore.selectedVisualElement?.sourceIndex;
        const hasSelectedSourceIndex = typeof selectedSourceIndex === 'number' && Number.isFinite(selectedSourceIndex);
	        const buildLayerOrderSubmenu = () => ({
	            title: "Layer order",
	            children: [
	                { title: "Bring to front", onClick: () => { CompositionService.bringVisualToFront(clipId, selectedSourceIndex); ContextMenuStore.hide(); } },
	                { title: "Bring forward", onClick: () => { CompositionService.bringVisualForward(clipId, selectedSourceIndex); ContextMenuStore.hide(); } },
	                { title: "Send backward", onClick: () => { CompositionService.sendVisualBackward(clipId, selectedSourceIndex); ContextMenuStore.hide(); } },
	                { title: "Send to back", onClick: () => { CompositionService.sendVisualToBack(clipId, selectedSourceIndex); ContextMenuStore.hide(); } },
	            ],
	        });

	        const getScriptClipLocalTimeAtAbsTime = (absTimeSec: number): number | null => {
	            const clip: any = CompositionService.getScriptLayerClip?.();
	            return resolveScriptLocalTimeForRecording(clip, absTimeSec);
	        };

	        const buildRecordIntoTranscriptMenuItem = (
	            source: "timeline-context-menu" | "script-clip-context-menu",
	            explicitScriptLocalSec?: number,
	        ): any | null => {
	            if (ApplicationStore.rangeToolEnabled) return null;
	            const scriptClip: any = CompositionService.getScriptLayerClip?.();
	            if (!scriptClip) return null;

	            const absoluteTime = Number(
	                contextSeekTimeSec ?? CompositionStateStore.currentTime ?? 0,
	            );
	            const requestedScriptLocalSec = Number.isFinite(explicitScriptLocalSec)
	                ? Number(explicitScriptLocalSec)
	                : getScriptClipLocalTimeAtAbsTime(absoluteTime);
	            if (requestedScriptLocalSec == null) return null;

	            const scriptClipId = String(scriptClip?.id ?? "") || null;
	            const transcriptWords = (ScriptLayerStore.transcriptWords ?? []).map((word: any) => ({
	                text: String(word?.text ?? ""),
	                payload: { ...word, clipId: scriptClipId ?? undefined },
	            }));
	            const anchor = resolveTimelineRecordAnchor(requestedScriptLocalSec, transcriptWords);
	            if (!anchor) return null;

	            const projectId = ProjectAssetsStore.projectId;
	            let engineCompositionId: string | null = null;
	            try {
	                engineCompositionId = (CompositionService.composition as any)?.compositionId ?? null;
	            } catch {}
	            const compositionId = resolveRecordIntoTranscriptCompositionId({
	                engineCompositionId,
	                projectCompositionId: ProjectAssetsStore.projectCurrentCompositionId,
	            }) || null;

	            const dispatch = (mediaKind: "audio" | "video") => {
	                dispatchRecordIntoTranscriptRequest({
	                    source,
	                    mediaKind,
	                    projectId,
	                    compositionId,
	                    scriptClipId,
	                    anchor,
	                });
	                ContextMenuStore.hide();
	            };

	            return {
	                title: "Record into transcript",
	                children: [
	                    { title: "Audio", onClick: () => dispatch("audio") },
	                    { title: "Video", onClick: () => dispatch("video") },
	                ],
	            };
	        };

	        if (isScriptLayerClip) {
	            // ─────────────────────────────────────────────────────────────────────
	            // Script Clip Context Menu
	            // ─────────────────────────────────────────────────────────────────────
	            if (!engineClip || dur <= 0 || !elementRef.current) {
	                return;
	            }

		            // Resolve right-click through the same semantic model used by hover and left-click.
		            const clipInSec = engineClip?.range?.[0]?.seconds ?? 0;
		            const clipOutSec = engineClip?.range?.[1]?.seconds ?? (engineClip?.duration?.seconds ?? clipInSec);
		            const EPS = 1e-3;

	            const sceneBoundarySplits = (engineClip?.splits ?? [])
	                .filter((s: any) => s?.isSceneBoundary === true)
	                .map((s: any) => s.timeSec)
	                .filter((t: any) => typeof t === 'number' && Number.isFinite(t) && t > clipInSec + EPS && t < clipOutSec - EPS)
	                .sort((a: number, b: number) => a - b);

	            const interactionElement = elementRef.current.querySelector<HTMLElement>(
	                '.script-clip__interaction-layer',
	            );
	            const rect = (interactionElement ?? elementRef.current).getBoundingClientRect();
	            const relX = e.clientX - rect.left;
		            const pct = rect.width > 0 ? (relX / rect.width) : 0;
		            const timeAtMouse = Math.max(0, Math.min(dur, pct * dur));
		            const model = buildScriptTimelineModel({
		                clip: engineClip,
		                duration: dur,
		                mapSourceBoundaryToTimeline: (sourceTime) =>
		                    ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(sourceTime),
		            });
		            const clickedSection = findScriptTimelineSectionAtTime(model.sections, timeAtMouse);
		            if (!clickedSection) return;

			            let gapInsertionAnchorSec: number | undefined;
			            if (clickedSection.kind === "gap" && clickedSection.gapInsertionId) {
			                const insertion = (engineClip?.gapInsertions ?? []).find(
			                    (candidate: any) =>
			                        String(candidate?.id ?? "") === String(clickedSection.gapInsertionId),
			                );
			                const insertionAt = Number(insertion?.atSec ?? Number.NaN);
			                if (Number.isFinite(insertionAt)) gapInsertionAnchorSec = insertionAt;
			            }
			            const recordIntoTranscriptMenuItem = buildRecordIntoTranscriptMenuItem(
			                "script-clip-context-menu",
			                gapInsertionAnchorSec,
			            );

		            ApplicationStore.setSelectedClipId(clipId);
		            ApplicationStore.selectScriptTimelineSection(clickedSection, model.sections);

		            if (clickedSection.kind === "gap") {
		                const gapScene =
	                    findScriptSceneByTimelineRange(
	                        engineClip,
	                        Number(clickedSection.start ?? 0),
	                        Number(clickedSection.end ?? 0),
	                        0.03,
	                    ) ?? null;

		                const insertionId = clickedSection.gapInsertionId;
	                if (insertionId) {
	                    menuItems.push({
	                        title: "Remove gap clip",
	                        onClick: () => {
	                            CompositionService.removeScriptLayerGapInsertion(insertionId);
	                            ApplicationStore.clearSelectedScriptTimelineSection();
	                            ContextMenuStore.hide();
	                        },
	                    });
	                }
	                if (gapScene && isScriptSceneEmpty(engineClip, gapScene)) {
	                    menuItems.push({
	                        title: "Add media",
	                        onClick: () => {
	                            beginScriptAddMediaFlow({
	                                atClipLocalSec: Number(gapScene.startSec ?? 0),
	                                preferExistingEmptyScene: true,
	                                source: "timeline-script-gap",
	                                sceneIndex: Number(gapScene.index ?? 0),
	                                sceneStartSec: Number(gapScene.startSec ?? 0),
	                                sceneEndSec: Number(gapScene.endSec ?? 0),
	                            });
	                            ContextMenuStore.hide();
	                        },
	                    });
	                }
	                if (recordIntoTranscriptMenuItem) {
	                    menuItems.unshift(recordIntoTranscriptMenuItem);
	                }

	                ContextMenuStore.show(e.clientX, e.clientY, menuItems);
	                return;
	            }

		            const contentSceneIndex = Number(clickedSection.sceneIndex ?? 0);
		            const sceneBoundaries: Array<{ start: number; end: number }> = [];
		            for (const section of model.sections) {
		                if (section.kind !== "scene" || section.sceneIndex == null) continue;
		                const current = sceneBoundaries[section.sceneIndex];
		                sceneBoundaries[section.sceneIndex] = current
		                    ? { start: Math.min(current.start, section.start), end: Math.max(current.end, section.end) }
		                    : { start: section.start, end: section.end };
		            }

			            const clipLocalBounds = [clipInSec, ...sceneBoundarySplits, clipOutSec];
	            
		            // Determine clip-local scene range (used by remove/delete actions).
		            const selectedSceneRangeClipLocal =
		                contentSceneIndex >= 0 && contentSceneIndex < clipLocalBounds.length - 1
	                    ? { start: clipLocalBounds[contentSceneIndex], end: clipLocalBounds[contentSceneIndex + 1] }
	                    : null;
	            const selectedEngineScene =
	                getScriptScenes(engineClip).find((scene) => Number(scene.index) === Number(contentSceneIndex)) ?? null;
	            const selectedSceneIsEmpty = Boolean(selectedEngineScene && isScriptSceneEmpty(engineClip, selectedEngineScene));
            
            const coversWholeClip = !selectedSceneRangeClipLocal ||
                (clipOutSec > clipInSec &&
                    Math.abs((selectedSceneRangeClipLocal.end - selectedSceneRangeClipLocal.start) - (clipOutSec - clipInSec)) < 0.001);

            const getClipLocalAtContext = (): number | null => {
                const abs = Number(contextSeekTimeSec ?? CompositionStateStore.currentTime ?? 0);
                if (!Number.isFinite(abs)) return null;
                let clipLocal: number | null = null;
                try {
                    if (typeof engineClip?.mapCompTimeToClipLocal === "function") {
                        clipLocal = engineClip.mapCompTimeToClipLocal(abs);
                    }
                } catch {}
                if (clipLocal == null) {
                    try {
                        if (typeof engineClip?.CompositionTimeToClipTime === "function") {
                            clipLocal = engineClip.CompositionTimeToClipTime(abs);
                        }
                    } catch {}
                }
                return clipLocal != null && Number.isFinite(clipLocal) ? Number(clipLocal) : null;
            };

            // Delete selected scene (delete whole clip if it's the only scene)
            const handleDeleteScene = () => {
                try {
                    const scenes: any[] = Array.isArray(engineClip?.getScenes?.()) ? engineClip.getScenes() : [];
                    if (coversWholeClip || scenes.length <= 1) {
                        CompositionService.deleteClipById(clipId);
                    } else {
                        CompositionService.deleteScriptLayerScene(contentSceneIndex, { transcriptMode: 'delete' });
                    }
                } catch {}
                ApplicationStore.clearSelectedScriptTimelineSection();
                ContextMenuStore.hide();
            };
            
            // 2. Cut
            // menuItems.push({
            //     title: "Cut",
            //     // icon: () => <ContentCutIcon/>,
            //     onClick: handleSceneRemove,
            // });
            
            // // 3. Copy (ignored for now)
            // menuItems.push({
            //     title: "Copy",
            //     // icon: () => <CopyIcon/>,
            //     onClick: () => { ContextMenuStore.hide(); },
            // });
            
			            // Case B (script scene selected) menu:
			            // Split scene at playhead, Add layer (scene-start anchored), Layer order,
			            // Add gap, Delete.
		            menuItems.push({
		                title: "Add a scene break here",
		                onClick: () => {
	                    const clipLocalTime = getClipLocalAtContext();
	                    if (clipLocalTime != null) {
	                        CompositionService.splitScriptLayer(clipLocalTime, true);
	                    }
	                    ContextMenuStore.hide();
	                },
	            });

	            // Add layer (anchored to the current scene start, constrained to scene end)
	            menuItems.push({
	                title: "Add layer",
	                onClick: () => {
	                    try {
		                    const sceneStartTimeline =
		                        sceneBoundaries?.[contentSceneIndex]?.start ??
	                            ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(selectedSceneRangeClipLocal?.start ?? clipInSec) ??
	                            0;
		                        ApplicationStore.setPendingAddLayerContext({ startAtSec: Number(sceneStartTimeline) || 0, constrainToSceneEnd: true });
		                    } catch {}
	                    try {
	                        window.dispatchEvent(new CustomEvent('openProjectSidebarFiles', { detail: { source: 'timeline' } }));
	                    } catch {}
	                    ContextMenuStore.hide();
	                },
	            });
	            if (selectedSceneIsEmpty && selectedEngineScene) {
	                menuItems.push({
	                    title: "Add media",
	                    onClick: () => {
	                        beginScriptAddMediaFlow({
	                            atClipLocalSec: Number(selectedEngineScene.startSec ?? 0),
	                            preferExistingEmptyScene: true,
	                            source: "timeline-script-scene",
	                            sceneIndex: Number(selectedEngineScene.index ?? 0),
	                            sceneStartSec: Number(selectedEngineScene.startSec ?? 0),
	                            sceneEndSec: Number(selectedEngineScene.endSec ?? 0),
	                        });
	                        ContextMenuStore.hide();
	                    },
	                });
	            }

		            if (isVisualClip && hasSelectedSourceIndex) {
		                menuItems.push(buildLayerOrderSubmenu());
		            }

			            // Add gap (inserted silence)
			            menuItems.push({
			                title: "Add gap",
			                onClick: () => {
		                    const clipLocalTime = getClipLocalAtContext();
		                    const insertionAt =
		                        selectedSceneRangeClipLocal?.end != null
		                            ? Number(selectedSceneRangeClipLocal.end)
		                            : clipLocalTime;
		                    if (insertionAt != null && Number.isFinite(insertionAt)) {
		                        CompositionService.insertEmptyScriptScene(insertionAt, 5.0);
		                    }
	                    ApplicationStore.clearSelectedScriptTimelineSection();
	                    ContextMenuStore.hide();
		                },
		            });

	            menuItems.push({
	                title: "Delete",
	                onClick: handleDeleteScene,
	            });
	            if (recordIntoTranscriptMenuItem) {
	                menuItems.unshift(recordIntoTranscriptMenuItem);
	            }

		        } else {
            // ─────────────────────────────────────────────────────────────────────
            // General Clip Context Menu
            // ─────────────────────────────────────────────────────────────────────

                    if (String((engineClip as any)?.type ?? "") === "placeholder") {
                        menuItems.push({
                            title: "Add Media",
                            onClick: () => {
                                try {
                                    ApplicationStore.setPendingPlaceholderReplaceContext({ placeholderClipId: clipId });
                                } catch {}
                                try {
                                    window.dispatchEvent(new CustomEvent("openProjectSidebarFiles", { detail: { source: "placeholder" } }));
                                } catch {}
                                ContextMenuStore.hide();
                            },
                        });
                    }

		            // Case A (non-script clip selected) menu:
		            // Add scene, Add layer, Layer order, Split clip, Delete clip.
		            menuItems.push({
		                title: "Add scene",
		                onClick: () => {
	                    const abs = Number(contextSeekTimeSec ?? CompositionStateStore.currentTime ?? 0);
	                    const clipLocal = getScriptClipLocalTimeAtAbsTime(abs);
	                    if (clipLocal != null) {
	                        CompositionService.splitScriptLayer(clipLocal, true);
	                    }
	                    ContextMenuStore.hide();
	                },
	            });

	            menuItems.push({
	                title: "Add layer",
	                onClick: () => {
	                    try {
	                        const t = Number(contextSeekTimeSec ?? CompositionStateStore.currentTime ?? 0);
	                        const sceneStart = getSceneStartAtTime(t) ?? 0;
	                        ApplicationStore.setPendingAddLayerContext({ startAtSec: sceneStart, constrainToSceneEnd: true });
	                    } catch {}
	                    try {
	                        window.dispatchEvent(new CustomEvent('openProjectSidebarFiles', { detail: { source: 'timeline' } }));
	                    } catch {}
	                    ContextMenuStore.hide();
	                },
	            });
            
            // 1. Cut
            // menuItems.push({
            //     title: "Cut",
            //     // icon: () => <ContentCutIcon/>,
            //     onClick: () => {
            //         CompositionService.deleteClipById(clipId);
            //         ContextMenuStore.hide();
            //     },
            // });
            
            // 2. Copy (ignored for now)
            // menuItems.push({
            //     title: "Copy",
            //     // icon: () => <CopyIcon/>,
            //     onClick: () => { ContextMenuStore.hide(); },
            // });
            
            // 3. Layer order (only for visual clips)
            if (isVisualClip) {
                menuItems.push(buildLayerOrderSubmenu());
            }
            
	            // 4. Split clip
	            menuItems.push({
	                title: "Split clip",
	                // icon: () => <ContentCutIcon/>,
	                onClick: () => {
	                    CompositionService.splitClip(contextSeekTimeSec ?? undefined);
	                    ContextMenuStore.hide();
	                },
	            });
            
	            // 5. Delete
	            menuItems.push({
	                title: "Delete clip",
	                // icon: () => <TrashIcon/>,
	                onClick: () => {
	                    CompositionService.deleteClipById(clipId);
	                    ContextMenuStore.hide();
	                },
	            });

	            const recordIntoTranscriptMenuItem = buildRecordIntoTranscriptMenuItem(
	                "timeline-context-menu",
	            );
	            if (recordIntoTranscriptMenuItem) {
	                menuItems.unshift(recordIntoTranscriptMenuItem);
	            }
        }

        ContextMenuStore.show(e.clientX, e.clientY, menuItems);
    };
    
    // Handle transition point handle click - open popover for that side
    const handleTransitionPointHandleClick = (
        tp: TransitionPointData, 
        side: 'fadeOut' | 'fadeIn',
        e: React.MouseEvent<HTMLDivElement>
    ) => {
        e.stopPropagation();
        e.preventDefault();
        setSelectedTpId(tp.id);
        setSelectedTpSide(side);
        const sideConfig = side === 'fadeOut' ? tp.fadeOut : tp.fadeIn;
        setTpDurationText(sideConfig.durationSec.toString());
        setTpType(sideConfig.type);
        setTpPopoverAnchor(e.currentTarget);
    };
    
    // Handle transition point right-click - show removal context menu
    const handleTransitionPointContextMenu = (tp: TransitionPointData, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        
        ContextMenuStore.show(e.clientX, e.clientY, [
            {
                title: "Remove transition point",
                icon: () => <RemoveCircleOutlineIcon/>,
                onClick: () => {
                    onTransitionPointRemove?.(tp.id);
                    ContextMenuStore.hide();
                }
            }
        ]);
    };
    
    // Close transition point popover
    const handleTpPopoverClose = () => {
        setTpPopoverAnchor(null);
        setSelectedTpId(null);
    };
    
    // Apply the current config to the transition point
    const applyTpConfig = (newType: 'none' | 'fade' | 'crossfade', durationSec: number) => {
        if (!selectedTpId || !onTransitionPointConfigChange) return;
        
        const config: TransitionSideConfig = { type: newType, durationSec };
        
        // If crossfade, also update the other side with same config
        if (newType === 'crossfade') {
            // Update both sides with crossfade
            onTransitionPointConfigChange(selectedTpId, 'fadeOut', config);
            onTransitionPointConfigChange(selectedTpId, 'fadeIn', config);
        } else {
            // Just update this side
            onTransitionPointConfigChange(selectedTpId, selectedTpSide, config);
        }
    };
    
    // Handle transition type change
    const handleTpTypeChange = (newType: 'none' | 'fade' | 'crossfade') => {
        setTpType(newType);
        const parsed = parseFloat(tpDurationText);
        applyTpConfig(newType, isNaN(parsed) ? 0 : parsed);
    };
    
    // Handle transition duration blur
    const handleTpDurationBlur = () => {
        const parsed = parseFloat(tpDurationText);
        if (!isNaN(parsed) && parsed >= 0) {
            applyTpConfig(tpType, parsed);
        }
    };
    
    // Handle transition duration keydown
    const handleTpDurationKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleTpDurationBlur();
            (e.target as HTMLInputElement).blur();
        }
    };

    // Only show words strip if wordsContent is actually provided (not null/undefined)
    const hasWordsStrip = wordsContent != null;

    // Fade overlay helpers (edge transitions)
    const hasAudioFadeInOverlay = Boolean(fadeInConfig && fadeInConfig.durationSec > 0 && trimmedDuration > 0);
    const hasAudioFadeOutOverlay = Boolean(fadeOutConfig && fadeOutConfig.durationSec > 0 && trimmedDuration > 0);
    const hasVisualFadeInOverlay = Boolean(
        visualFadeInConfig &&
        visualFadeInConfig.type !== 'none' &&
        visualFadeInConfig.durationSec > 0 &&
        trimmedDuration > 0
    );
    const hasVisualFadeOutOverlay = Boolean(
        visualFadeOutConfig &&
        visualFadeOutConfig.type !== 'none' &&
        visualFadeOutConfig.durationSec > 0 &&
        trimmedDuration > 0
    );
    const splitFadeInOverlay = hasAudioFadeInOverlay && hasVisualFadeInOverlay;
    const splitFadeOutOverlay = hasAudioFadeOutOverlay && hasVisualFadeOutOverlay;
    const audioFadeInWidthPct = hasAudioFadeInOverlay
        ? Math.min(100, (fadeInConfig!.durationSec / trimmedDuration) * 100)
        : 0;
    const audioFadeOutWidthPct = hasAudioFadeOutOverlay
        ? Math.min(100, (fadeOutConfig!.durationSec / trimmedDuration) * 100)
        : 0;
    const visualFadeInWidthPct = hasVisualFadeInOverlay
        ? Math.min(100, (visualFadeInConfig!.durationSec / trimmedDuration) * 100)
        : 0;
    const visualFadeOutWidthPct = hasVisualFadeOutOverlay
        ? Math.min(100, (visualFadeOutConfig!.durationSec / trimmedDuration) * 100)
        : 0;

    return (
        <div
            ref={elementRef}
            id={clipId}
            data-tour="timeline-clip"
            style={clipStyle}
            className={clsx(CLIP_CLASSNAME, generateClipIdentifierClass(clipId), {
                // Script clip selection is shown via per-section overlay (not whole-clip outline).
                clip__focused: isFocused && !isScriptLayerClip,
                clip__predecessor: hasPredecessor,
                clip__successor: hasSuccessor,
                'clip--with-words': hasWordsStrip,
                'clip--script-layer': isScriptLayerClip,
                'clip--media': clipVariant === 'media',
                [`clip--${clipKind}`]: clipKind,
                'clip--with-thumbnail': thumbnail != null,
                'clip--resize-enabled': !isScriptLayerClip && (isResizeEnabled ?? true),
                'clip--range': ApplicationStore.rangeToolEnabled,
                'clip--slip': isSlipMode,
            })}
            onMouseDown={handleMouseDown}
            onContextMenu={handleContextMenu}
            onMouseEnter={() => {
                if (isSlipMode) setIsSlipHovering(true);
            }}
            onMouseLeave={() => {
                setIsSlipHovering(false);
            }}
        >
            {(isSlipHovering || isSlipDragging) && slipRangeText && (
                <div className="clip__slip-range" aria-label="Slip range">
                    {slipRangeText}
                </div>
            )}
            {/* Audio/Visual transition handles */}
            {showAudioTransitions && onFadeInChange && (
                <TransitionHandle
                    side="in"
                    currentConfig={fadeInConfig}
                    hasAdjacentClip={hasAdjacentBefore}
                    onConfigChange={onFadeInChange}
                    offsetPercent={fadeInConfig && trimmedDuration > 0 
                        ? Math.min(50, (fadeInConfig.durationSec / trimmedDuration) * 100) 
                        : 0}
                    showVisual={showVisualTransitions}
                    visualConfig={visualFadeInConfig}
                    onVisualConfigChange={onVisualFadeInChange}
                />
            )}
            {showAudioTransitions && onFadeOutChange && (
                <TransitionHandle
                    side="out"
                    currentConfig={fadeOutConfig}
                    hasAdjacentClip={hasAdjacentAfter}
                    onConfigChange={onFadeOutChange}
                    offsetPercent={fadeOutConfig && trimmedDuration > 0 
                        ? Math.min(50, (fadeOutConfig.durationSec / trimmedDuration) * 100) 
                        : 0}
                    showVisual={showVisualTransitions}
                    visualConfig={visualFadeOutConfig}
                    onVisualConfigChange={onVisualFadeOutChange}
                />
            )}
            
            {/* Visual fade-in overlay (triangular shading at clip start) */}
            {hasVisualFadeInOverlay && (
                <div 
                    className="clip__fade-overlay clip__fade-overlay--in"
                    style={{ 
                        width: `${visualFadeInWidthPct}%`,
                        bottom: splitFadeInOverlay ? '50%' : 0,
                    }}
                >
                    <svg 
                        viewBox="0 0 100 100" 
                        preserveAspectRatio="none"
                        style={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            width: '100%', 
                            height: '100%' 
                        }}
                    >
                        {/* Triangle: left edge + top edge + diagonal from bottom-left to top-right (dot) */}
                        <polygon 
                            points="0,0 100,0 0,100" 
                            fill="rgba(0,0,0,0.18)"
                        />
                        {/* Diagonal line from bottom-left to top-right (where dot is) */}
                        <line 
                            x1="0" y1="100" 
                            x2="100" y2="0"
                            stroke="rgba(255,255,255,0.55)"
                            strokeWidth="1"
                            vectorEffect="non-scaling-stroke"
                        />
                    </svg>
                </div>
            )}

            {/* Audio fade-in overlay (triangular shading at clip start) */}
            {hasAudioFadeInOverlay && (
                <div 
                    className="clip__fade-overlay clip__fade-overlay--in"
                    style={{ 
                        width: `${audioFadeInWidthPct}%`,
                        top: splitFadeInOverlay ? '50%' : 0,
                    }}
                >
                    <svg 
                        viewBox="0 0 100 100" 
                        preserveAspectRatio="none"
                        style={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            width: '100%', 
                            height: '100%' 
                        }}
                    >
                        {/* Triangle: left edge + top edge + diagonal from bottom-left to top-right (dot) */}
                        <polygon 
                            points="0,0 100,0 0,100" 
                            fill="rgba(0,0,0,0.3)"
                        />
                        {/* Diagonal line from bottom-left to top-right (where dot is) */}
                        <line 
                            x1="0" y1="100" 
                            x2="100" y2="0"
                            stroke="rgba(255,255,255,0.7)"
                            strokeWidth="1"
                            vectorEffect="non-scaling-stroke"
                        />
                    </svg>
                </div>
            )}
            
            {/* Visual fade-out overlay (triangular shading at clip end) */}
            {hasVisualFadeOutOverlay && (
                <div 
                    className="clip__fade-overlay clip__fade-overlay--out"
                    style={{ 
                        width: `${visualFadeOutWidthPct}%`,
                        bottom: splitFadeOutOverlay ? '50%' : 0,
                    }}
                >
                    <svg 
                        viewBox="0 0 100 100" 
                        preserveAspectRatio="none"
                        style={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            width: '100%', 
                            height: '100%' 
                        }}
                    >
                        {/* Triangle: right edge + top edge + diagonal from bottom-right to top-left (dot) */}
                        <polygon 
                            points="0,0 100,0 100,100" 
                            fill="rgba(0,0,0,0.18)"
                        />
                        {/* Diagonal line from bottom-right to top-left (where dot is) */}
                        <line 
                            x1="100" y1="100" 
                            x2="0" y2="0"
                            stroke="rgba(255,255,255,0.55)"
                            strokeWidth="1"
                            vectorEffect="non-scaling-stroke"
                        />
                    </svg>
                </div>
            )}

            {/* Audio fade-out overlay (triangular shading at clip end) */}
            {hasAudioFadeOutOverlay && (
                <div 
                    className="clip__fade-overlay clip__fade-overlay--out"
                    style={{ 
                        width: `${audioFadeOutWidthPct}%`,
                        top: splitFadeOutOverlay ? '50%' : 0,
                    }}
                >
                    <svg 
                        viewBox="0 0 100 100" 
                        preserveAspectRatio="none"
                        style={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            width: '100%', 
                            height: '100%' 
                        }}
                    >
                        {/* Triangle: right edge + top edge + diagonal from bottom-right to top-left (dot) */}
                        <polygon 
                            points="0,0 100,0 100,100" 
                            fill="rgba(0,0,0,0.3)"
                        />
                        {/* Diagonal line from bottom-right to top-left (where dot is) */}
                        <line 
                            x1="100" y1="100" 
                            x2="0" y2="0"
                            stroke="rgba(255,255,255,0.7)"
                            strokeWidth="1"
                            vectorEffect="non-scaling-stroke"
                        />
                    </svg>
                </div>
            )}
            
            {/* Words strip - thin section at top for transcript words */}
            {hasWordsStrip && (
                <div className="clip__words-strip">
                    {wordsContent}
                </div>
            )}
            
            {/* Main content area - waveform, thumbnail, etc. */}
            <div className="clip__content">
                {thumbnail != null && (
                    <div className="clip__thumbnail-container">
                        <div className="clip__thumbnail" style={{ backgroundImage: `url(${thumbnail})` }} />
                    </div>
                )}

                {children}

                <div className="clip__content__text">
                    <b>{filename}</b>
                </div>
            </div>
            
            {/* Transition point fade overlays */}
            {transitionPoints.map((tp) => {
                const fadeOutDuration = tp.fadeOut.durationSec || 0;
                const fadeInDuration = tp.fadeIn.durationSec || 0;
                const fadeOutWidth = trimmedDuration > 0 ? Math.min(tp.position, fadeOutDuration / trimmedDuration) : 0;
                const fadeInWidth = trimmedDuration > 0 ? Math.min(1 - tp.position, fadeInDuration / trimmedDuration) : 0;
                
                return (
                    <React.Fragment key={`${tp.id}-overlays`}>
                        {/* Fade-out overlay (before transition point) */}
                        {fadeOutDuration > 0 && fadeOutWidth > 0 && (
                            <div 
                                className="clip__fade-overlay clip__fade-overlay--tp-out"
                                style={{ 
                                    right: `${(1 - tp.position) * 100}%`,
                                    width: `${fadeOutWidth * 100}%`,
                                }}
                            >
                                <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                                    <polygon points="0,0 100,0 100,100" fill="rgba(0,0,0,0.3)" />
                                    <line x1="100" y1="100" x2="0" y2="0" stroke="rgba(255,255,255,0.7)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                                </svg>
                            </div>
                        )}
                        {/* Fade-in overlay (after transition point) */}
                        {fadeInDuration > 0 && fadeInWidth > 0 && (
                            <div 
                                className="clip__fade-overlay clip__fade-overlay--tp-in"
                                style={{ 
                                    left: `${tp.position * 100}%`,
                                    width: `${fadeInWidth * 100}%`,
                                }}
                            >
                                <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                                    <polygon points="0,0 100,0 0,100" fill="rgba(0,0,0,0.3)" />
                                    <line x1="0" y1="100" x2="100" y2="0" stroke="rgba(255,255,255,0.7)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                                </svg>
                            </div>
                        )}
                    </React.Fragment>
                );
            })}
            
            {/* Transition point markers - outside content to span full clip height */}
            {transitionPoints.map((tp) => {
                const fadeOutDuration = tp.fadeOut.durationSec || 0;
                const fadeInDuration = tp.fadeIn.durationSec || 0;
                const fadeOutOffset = trimmedDuration > 0 ? Math.min(tp.position * 100, (fadeOutDuration / trimmedDuration) * 100) : 0;
                const fadeInOffset = trimmedDuration > 0 ? Math.min((1 - tp.position) * 100, (fadeInDuration / trimmedDuration) * 100) : 0;
                
                return (
                <div
                    key={tp.id}
                    className="clip__transition-point"
                    style={{ left: `${tp.position * 100}%` }}
                    onContextMenu={(e) => handleTransitionPointContextMenu(tp, e)}
                >
                    <div className="clip__transition-point__line" />
                    <div className="clip__transition-point__handles">
                        {/* Left handle - fade out (end of segment A) - moves left based on duration */}
                        <div
                            className={clsx('clip__transition-point__handle', {
                                'clip__transition-point__handle--active': tp.fadeOut.type === 'fade' && fadeOutDuration > 0,
                                'clip__transition-point__handle--crossfade': tp.fadeOut.type === 'crossfade' && fadeOutDuration > 0,
                            })}
                            style={fadeOutOffset > 0 ? { transform: `translateX(calc(-${fadeOutOffset}% * ${width / 100}px - 50%))` } : undefined}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => handleTransitionPointHandleClick(tp, 'fadeOut', e)}
                            title="Fade out - click to configure"
                        />
                        {/* Right handle - fade in (start of segment B) - moves right based on duration */}
                        <div
                            className={clsx('clip__transition-point__handle', {
                                'clip__transition-point__handle--active': tp.fadeIn.type === 'fade' && fadeInDuration > 0,
                                'clip__transition-point__handle--crossfade': tp.fadeIn.type === 'crossfade' && fadeInDuration > 0,
                            })}
                            style={fadeInOffset > 0 ? { transform: `translateX(calc(${fadeInOffset}% * ${width / 100}px + 50%))` } : undefined}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => handleTransitionPointHandleClick(tp, 'fadeIn', e)}
                            title="Fade in - click to configure"
                        />
                    </div>
                </div>
                );
            })}
            
            {/* Transition point configuration popover */}
            <Popover
                open={Boolean(tpPopoverAnchor)}
                anchorEl={tpPopoverAnchor}
                onClose={handleTpPopoverClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '8px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            minWidth: '200px',
                            p: 1.5,
                        }
                    }
                }}
            >
                <Box 
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Visual Transitions - shown for video/image clips */}
                    {showVisualTransitions && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'var(--app-text-subtle)', textTransform: 'uppercase' }}>
                                        {selectedTpSide === 'fadeOut' ? 'OUT' : 'IN'}
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--app-text-primary)', fontSize: '13px' }}>
                                        Visual
                                    </Typography>
                                </Box>
                                {tpVisualType !== 'none' && (
                                    <IconButton 
                                        size="small" 
                                        onClick={() => setTpVisualType('none')}
                                        sx={{ p: 0.25, color: 'var(--app-text-subtle)', '&:hover': { color: '#EF4444' } }}
                                    >
                                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                )}
                            </Box>
                            
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={tpVisualType}
                                    onChange={(e) => setTpVisualType(e.target.value as VisualTransitionType)}
                                    sx={{ fontSize: '13px', '& .MuiSelect-select': { py: 0.75 } }}
                                >
                                    <MenuItem value="none" sx={{ fontSize: '13px' }}>None</MenuItem>
                                    <MenuItem value="crossfade" sx={{ fontSize: '13px' }}>Crossfade</MenuItem>
                                    <MenuItem value="wipe" sx={{ fontSize: '13px' }}>Wipe</MenuItem>
                                    <MenuItem value="fade" sx={{ fontSize: '13px' }}>Fade (to/from black)</MenuItem>
                                    <MenuItem value="blur" sx={{ fontSize: '13px' }}>Blur</MenuItem>
                                </Select>
                            </FormControl>
                            
                            {tpVisualType !== 'none' && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: '12px', color: 'var(--app-text-muted)', whiteSpace: 'nowrap' }}>Duration</Typography>
                                    <TextField
                                        size="small"
                                        value={tpVisualDurationText}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^[0-9]*\.?[0-9]*$/.test(value) || value === '') {
                                                setTpVisualDurationText(value);
                                            }
                                        }}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: '12px', color: 'var(--app-text-subtle)' }}>s</Typography></InputAdornment>,
                                            sx: { fontSize: '13px', '& input': { py: 0.5, px: 1, textAlign: 'right' } },
                                        }}
                                        sx={{ width: '80px' }}
                                    />
                                </Box>
                            )}
                            
                            {tpVisualType === 'wipe' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography sx={{ fontSize: '12px', color: 'var(--app-text-muted)' }}>Direction</Typography>
                                    <ToggleButtonGroup
                                        value={tpVisualWipeDirection}
                                        exclusive
                                        onChange={(_, val) => val && setTpVisualWipeDirection(val)}
                                        size="small"
                                        sx={{ '& .MuiToggleButton-root': { py: 0.5, px: 1 }, '& .Mui-selected': { backgroundColor: '#3B82F6', color: '#fff' } }}
                                    >
                                        <ToggleButton value="left"><ArrowBackIcon sx={{ fontSize: 16 }} /></ToggleButton>
                                        <ToggleButton value="right"><ArrowForwardIcon sx={{ fontSize: 16 }} /></ToggleButton>
                                        <ToggleButton value="up"><ArrowUpwardIcon sx={{ fontSize: 16 }} /></ToggleButton>
                                        <ToggleButton value="down"><ArrowDownwardIcon sx={{ fontSize: 16 }} /></ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                            )}
                            
                            {tpVisualType === 'blur' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: '12px', color: 'var(--app-text-muted)' }}>Blur Amount</Typography>
                                        <Typography sx={{ fontSize: '12px', color: 'var(--app-text-primary)', fontWeight: 500 }}>{tpVisualBlurAmount}</Typography>
                                    </Box>
                                    <Slider value={tpVisualBlurAmount} onChange={(_, val) => setTpVisualBlurAmount(val as number)} min={1} max={1000} size="small" />
                                </Box>
                            )}
                            
                            <Divider sx={{ my: 0.5 }} />
                        </>
                    )}
                    
                    {/* Audio Header with delete button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'var(--app-text-subtle)', textTransform: 'uppercase' }}>
                                {selectedTpSide === 'fadeOut' ? 'OUT' : 'IN'}
                            </Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'var(--app-text-primary)', fontSize: '13px' }}>
                                Audio
                            </Typography>
                        </Box>
                        {tpType !== 'none' && (
                            <IconButton 
                                size="small" 
                                onClick={() => handleTpTypeChange('none')}
                                sx={{ p: 0.25, color: 'var(--app-text-subtle)', '&:hover': { color: '#EF4444' } }}
                            >
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        )}
                    </Box>
                    
                    {/* Type Selector */}
                    <FormControl size="small" fullWidth>
                        <Select
                            value={tpType}
                            onChange={(e) => handleTpTypeChange(e.target.value as 'none' | 'fade' | 'crossfade')}
                            sx={{
                                fontSize: '13px',
                                '& .MuiSelect-select': {
                                    py: 0.75,
                                },
                            }}
                        >
                            <MenuItem value="none" sx={{ fontSize: '13px' }}>
                                None
                            </MenuItem>
                            <MenuItem value="fade" sx={{ fontSize: '13px' }}>
                                Fade
                            </MenuItem>
                            <MenuItem value="crossfade" sx={{ fontSize: '13px' }}>
                                Crossfade
                            </MenuItem>
                        </Select>
                    </FormControl>
                    
                    {/* Duration Input - only show if not 'none' */}
                    {tpType !== 'none' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography 
                                sx={{ 
                                    fontSize: '12px', 
                                    color: 'var(--app-text-muted)',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Duration
                            </Typography>
                            <TextField
                                size="small"
                                value={tpDurationText}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (/^[0-9]*\.?[0-9]*$/.test(value) || value === '') {
                                        setTpDurationText(value);
                                    }
                                }}
                                onBlur={handleTpDurationBlur}
                                onKeyDown={handleTpDurationKeyDown}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Typography sx={{ fontSize: '12px', color: 'var(--app-text-subtle)' }}>
                                                s
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        fontSize: '13px',
                                        '& input': {
                                            py: 0.5,
                                            px: 1,
                                            textAlign: 'right',
                                        },
                                    },
                                }}
                                sx={{
                                    width: '80px',
                                }}
                            />
                        </Box>
                    )}
                    
                    {/* Crossfade note */}
                    {tpType === 'crossfade' && (
                        <Typography 
                            sx={{ 
                                fontSize: '11px', 
                                color: 'var(--app-text-subtle)',
                                fontStyle: 'italic',
                            }}
                        >
                            Syncs both sides
                        </Typography>
                    )}
                </Box>
            </Popover>
        </div>
    );
});

export default Clip;
